import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import apiService from '../../services/api';
import { AdminPageLayout, ConfirmDialog, EmptyState, FilterBar } from '../../components';
import { generateNestedBreadcrumbs } from '../../utils/breadcrumbGenerator';
// import TeamTryouts from '../../components/TeamTryouts'; // REMOVED - Tryouts disabled
import './ProgramTeams.css';

const ProgramTeams = () => {
  const { programId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [program, setProgram] = useState(null);
  const [teams, setTeams] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  // const [selectedTeam, setSelectedTeam] = useState(null); // REMOVED - Tryouts disabled
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState(null);

  useEffect(() => {
    loadData();
  }, [programId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [programData, teamsData] = await Promise.all([
        apiService.getProgram(programId),
        apiService.getProgramTeams(programId)
      ]);

      setProgram(programData);
      setTeams(teamsData.teams || []);
    } catch (err) {
      setError(err.message || 'Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTeam = async () => {
    if (!teamToDelete) return;

    try {
      setLoading(true);
      await apiService.deleteTeam(teamToDelete.id);
      await loadData();
      setShowDeleteConfirm(false);
      setTeamToDelete(null);
    } catch (err) {
      setError(err.message || 'Failed to delete team');
    } finally {
      setLoading(false);
    }
  };

  const filteredTeams = teams.filter(team =>
    team.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    team.age_group_label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    team.season?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Generate breadcrumbs with program context
  const breadcrumbItems = program ? generateNestedBreadcrumbs(
    window.location.pathname,
    [
      { label: 'Programs', path: '/admin/programs' },
      { label: program.name, path: '/admin/programs' },
      { label: 'Teams', path: null }
    ]
  ) : null;

  if (loading && !program) {
    return (
      <div className="program-teams-page">
        <div className="loading">Loading teams...</div>
      </div>
    );
  }

  const primaryActions = (
    <Link
      to={`/admin/programs/${programId}/teams/new`}
      className="button primary"
    >
      + Add Team
    </Link>
  );

  return (
    <AdminPageLayout
      title="PROGRAM TEAMS"
      subtitle="Manage teams in this program"
      backPath="/admin/programs"
      primaryActions={primaryActions}
      breadcrumbs={breadcrumbItems}
    >

      <div className="program-context" style={{ marginBottom: '1rem', padding: '0.75rem', background: 'var(--color-background-lighter)', borderRadius: '4px' }}>
        <div style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>{program?.name}</div>
        <div style={{ color: 'var(--color-text-tertiary)', fontSize: '0.9rem' }}>{program?.category} • {program?.sport}</div>
      </div>

      {error && (
        <div className="error-banner">
          <strong>Error:</strong> {error}
        </div>
      )}

      <FilterBar
        filters={[
          {
            type: 'search',
            name: 'search',
            placeholder: 'Search teams by name, age group, or season...',
            value: searchTerm,
            onChange: (e) => setSearchTerm(e.target.value)
          }
        ]}
        onClear={() => setSearchTerm('')}
      >
        <div className="teams-count">
          {filteredTeams.length} {filteredTeams.length === 1 ? 'team' : 'teams'}
        </div>
      </FilterBar>

      {loading ? (
        <div className="loading-state">Loading teams...</div>
      ) : filteredTeams.length === 0 ? (
        <EmptyState
          icon="fas fa-users"
          title="No Teams Found"
          description={
            searchTerm
              ? 'Try adjusting your search criteria'
              : 'Get started by creating your first team for this program'
          }
        >
          {!searchTerm && (
            <Link
              to={`/admin/programs/${programId}/teams/new`}
              className="button primary"
            >
              Create First Team
            </Link>
          )}
        </EmptyState>
      ) : (
        <div className="teams-list">
          {filteredTeams.map(team => (
            <div key={team.id} className="team-card">
              <div className="team-header">
                <div className="team-title">
                  <h3>{team.name}</h3>
                  {team.age_group_label && (
                    <span className="age-badge">{team.age_group_label}</span>
                  )}
                  {team.status && (
                    <span className={`status-badge status-${team.status}`}>
                      {team.status}
                    </span>
                  )}
                </div>
                <div className="team-actions">
                  {/* TRYOUT BUTTON REMOVED - Tryouts disabled */}
                  {/* <button
                    onClick={() => setSelectedTeam(team)}
                    className="button small primary"
                    title="Open tryouts for this team"
                  >
                    Open Tryout
                  </button> */}
                  <Link
                    to={`/admin/programs/${programId}/teams/${team.id}/edit`}
                    className="button small secondary"
                  >
                    Edit
                  </Link>
                  <button
                    onClick={() => {
                      setTeamToDelete(team);
                      setShowDeleteConfirm(true);
                    }}
                    className="button small danger"
                    disabled={loading}
                  >
                    Delete
                  </button>
                </div>
              </div>

              <div className="team-details">
                {team.season && (
                  <div className="detail-item">
                    <span className="detail-label">Season:</span>
                    <span className="detail-value">{team.season}</span>
                  </div>
                )}
                {team.max_capacity && (
                  <div className="detail-item">
                    <span className="detail-label">Capacity:</span>
                    <span className="detail-value">
                      {team.current_enrollment || 0} / {team.max_capacity}
                    </span>
                  </div>
                )}
                {team.start_date && (
                  <div className="detail-item">
                    <span className="detail-label">Start Date:</span>
                    <span className="detail-value">
                      {new Date(team.start_date).toLocaleDateString()}
                    </span>
                  </div>
                )}
                {team.end_date && (
                  <div className="detail-item">
                    <span className="detail-label">End Date:</span>
                    <span className="detail-value">
                      {new Date(team.end_date).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>

              {team.coach_assignments && team.coach_assignments.length > 0 && (
                <div className="team-coaches">
                  <span className="coaches-label">Coaches:</span>
                  {team.coach_assignments.map((assignment, idx) => (
                    <span key={idx} className="coach-badge">
                      {assignment.coach_name || assignment.coach_id}
                      {assignment.role && ` (${assignment.role})`}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* TRYOUT SECTION REMOVED - Tryouts disabled */}
      {/* {selectedTeam && (
        <div className="tryout-section-wrapper">
          <div className="tryout-section-header">
            <h2>Tryouts for {selectedTeam.name}</h2>
            <button
              onClick={() => setSelectedTeam(null)}
              className="button small secondary"
            >
              Close
            </button>
          </div>
          <TeamTryouts
            teamId={selectedTeam.id}
            teamName={selectedTeam.name}
            season={selectedTeam.season}
          />
        </div>
      )} */}

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setTeamToDelete(null);
        }}
        onConfirm={handleDeleteTeam}
        title="Delete Team"
        message={`Are you sure you want to delete "${teamToDelete?.name}"?`}
        details="This action cannot be undone."
        variant="danger"
        confirmText="Delete"
        cancelText="Cancel"
        loading={loading}
      />
    </AdminPageLayout>
  );
};

export default ProgramTeams;
